import { lt as semverLt, valid as semverValid } from 'semver';
import { isNullable } from '../types/isNullable';
import type { BaseCloudConstructLicense } from './baseCloudConstruct';
import { type LicenseCcuStrategy, verifyLicenseData } from './licenseSchema';

const FIRST_CCU_VERSION = '8.1.0';

// vCollect.ipfVersion looks like "8.0.4+0"; the build metadata is valid semver
// and ignored when comparing. An unparseable version counts as pre-CCU — we
// cannot confirm the snapshot is new enough, and claiming CCU data for it is
// the worse of the two mistakes.
function isPreCcuVersion(ipfVersion: string): boolean {
  const parsed = semverValid(ipfVersion);
  return parsed === null || semverLt(parsed, FIRST_CCU_VERSION);
}

// What was measured. `devices` is a row count, `ccu` a SUM over a numeric
// column, which is why only the latter is a string -- see LicenseUsage.
//
// Nullable because callers often build a checker before a license is known
// (frontend still loading, no snapshot selected). A null ccu means "no
// CCU-priced row exists at all", which is distinct from a real cost of 0.
export type ArtifactCount = {
  devices: number | null | undefined;
  ccu?: string | null;
};

// What was bought. Both figures are parsed out of the license file as JSON
// numbers, so this deliberately does not extend ArtifactCount: a purchased
// ceiling and a measured spend are different things that happen to share
// field names.
export type LicenseLimits = {
  devices: number | null | undefined;
  ccu?: number | null;
  ccuStrategy?: LicenseCcuStrategy;
};

export class LicenseLimitChecker {
  constructor(private readonly license: LicenseLimits) {}

  static fromJson(data: unknown): LicenseLimitChecker {
    const result = verifyLicenseData(data);
    if (!result.success) {
      throw result.error;
    }
    return new LicenseLimitChecker(result.data);
  }

  // An unknown limit or an unknown count can't be said to exceed anything.
  lteDeviceLimit(info: ArtifactCount): boolean {
    if (isNullable(this.license.devices)) {
      return true;
    }
    if (isNullable(info.devices)) {
      return true;
    }
    return info.devices <= this.license.devices;
  }

  // Under the ccu strategy the cloud spend has its own ceiling. Under any other
  // one there is no ccu ceiling at all: adjustCcuCostIfNeeded has nulled every
  // construct's cost, so the spend lands in the device count and is compared
  // against the device limit instead. The `?? 0` covers exactly that case —
  // ccu is null/undefined there, and adding it raw would yield NaN.
  //
  // The spend arrives as a string and is converted here, the one point where it
  // is computed with rather than copied. Doing it here rather than at each call
  // site keeps `Number(null) === 0` from turning "no CCU-priced row exists"
  // into "the spend was zero" -- two different statements that the branches
  // below answer differently.
  lteCcuLimit(info: ArtifactCount): boolean {
    const spend = isNullable(info.ccu) ? null : Number(info.ccu);

    if (this.license.ccuStrategy === 'ccu') {
      if (isNullable(this.license.ccu) || isNullable(spend)) {
        return true;
      }
      return spend <= this.license.ccu;
    }

    if (isNullable(this.license.devices) || isNullable(info.devices)) {
      return true;
    }
    return (spend ?? 0) + info.devices <= this.license.devices;
  }

  get deviceLimit(): number | null {
    return this.license.devices ?? null;
  }

  get ccuLimit(): number | null {
    return this.license.ccu ?? null;
  }

  // Null on vCollect means "this snapshot has no CCU data", which the UI shows
  // as a placeholder rather than as zero usage. Recording a 0 instead would
  // claim the customer used no CCU, which is a different statement.
  //
  // Called with no argument by a live discovery, which by definition runs the
  // current version, and with a stored snapshot's version otherwise. The two
  // are separate parameter shapes rather than one nullable argument because a
  // stored version of null means the snapshot predates versioning entirely --
  // the opposite answer from an omitted argument.
  shouldRecordCcuOnVCollect(
    ...snapshotVersion: [] | [ipfVersion: string | null]
  ): boolean {
    if (this.license.ccuStrategy !== 'ccu') {
      return false;
    }
    if (snapshotVersion.length === 0) {
      return true;
    }
    const [ipfVersion] = snapshotVersion;
    return !isNullable(ipfVersion) && !isPreCcuVersion(ipfVersion);
  }

  // Null (not 0) so getLicenseUsage's "ccu IS NULL" branch folds this
  // construct into the plain device count instead of the CCU-priced count.
  adjustCcuCostIfNeeded(construct: BaseCloudConstructLicense): void {
    if (this.license.ccuStrategy !== 'ccu') {
      construct.ccu = null;
    }
  }
}
